import { format as formatLocalized } from '@/utils/date-fns/format'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { parseISO } from 'date-fns'
import { ArrowLeftIcon } from 'lucide-react'
import { useMemo } from 'react'
import { InfoLabel } from '../driver-grade/grade-parts'
import { DriverWorkDays } from './driver-work-days'
import {
  filterDrivers,
  useDriverWorkFilter,
  useDriverWorkFilterForm,
} from './driver-work-filter'
import { DriverWorkLayout, useDrillDown } from './driver-work-header'
import { DriverWorkYearTable } from './driver-work-year-table'
import { formatHours, perWeek } from './hours'
import { DRIVER_WORK_WEEKS_QUERY, DRIVER_WORK_YEAR_QUERY } from './queries'
import { WorkBars, WorkLegend } from './work-bars'

const Stat: React.FC<{ label: React.ReactNode; value: string }> = ({
  label,
  value,
}) => (
  <div className="space-y-1 px-5 py-4">
    <div className="text-xs font-semibold uppercase text-stone-500">
      {label}
    </div>
    <div className="text-lg font-semibold tabular-nums text-stone-900">
      {value}
    </div>
  </div>
)

const DriverYear: React.FC<{
  user: string
  year: number
  week: string | null
  onSelectWeek(week: string): void
}> = ({ user, year, week, onSelectWeek }) => {
  const translations = useTranslate({
    total: 'driver-work.total',
    totalInfo: 'driver-work.total-info',
    days: 'driver-work.days-worked',
    weeks: 'driver-work.weeks-worked',
    perWeek: 'driver-work.per-week',
    weekByWeek: 'driver-work.week-by-week',
    selectWeek: 'driver-work.select-week',
    week: 'common.week',
  })
  const { ownedVehiclesOnly } = useDriverWorkFilter().filter
  const { data: yearData } = useQuery(DRIVER_WORK_YEAR_QUERY, {
    variables: { year, ownedVehiclesOnly },
  })
  const { data: current, previousData } = useQuery(DRIVER_WORK_WEEKS_QUERY, {
    variables: { user, year, ownedVehiclesOnly },
  })
  const data = current ?? previousData
  const row = yearData?.driverWorkYear.find(r => r.user.id === user)
  const weeks = data?.driverWorkWeeks ?? []

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 divide-stone-200 overflow-hidden rounded-2xl bg-white shadow-card md:grid-cols-4 md:divide-x">
        <Stat
          label={
            <InfoLabel
              label={translations.total}
              info={translations.totalInfo}
            />
          }
          value={formatHours(row?.totalHours ?? 0)}
        />
        <Stat label={translations.days} value={String(row?.days ?? 0)} />
        <Stat label={translations.weeks} value={String(row?.weeks ?? 0)} />
        <Stat
          label={translations.perWeek}
          value={formatHours(perWeek(row?.totalHours ?? 0, row?.weeks ?? 0))}
        />
      </div>

      <div className="space-y-3 rounded-2xl bg-white px-5 py-4 shadow-card">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="text-xs font-semibold uppercase text-stone-500">
            {translations.weekByWeek}
          </div>
          <div className="text-xs text-stone-500">
            {translations.selectWeek}
          </div>
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-[640px]">
            <WorkBars
              labelEvery={4}
              selected={week}
              onSelect={onSelectWeek}
              bars={weeks.map(w => ({
                key: w.weekStart,
                label: String(w.isoWeek),
                title: `${translations.week} ${w.isoWeek} (${formatLocalized(
                  parseISO(w.weekStart),
                  'd. MMM'
                )}): ${formatHours(w.totalHours)}`,
                driveHours: w.driveHours,
                workHours: w.workHours,
                availableHours: w.availableHours,
              }))}
            />
          </div>
        </div>
        <WorkLegend />
      </div>

      {week && <DriverWorkDays key={week} user={user} weekStart={week} />}
    </div>
  )
}

export const DriverWorkPage: React.FC = () => {
  const translations = useTranslate({
    allDrivers: 'driver-work.all-drivers',
  })
  const form = useDriverWorkFilterForm()
  const { year, drivers, ownedVehiclesOnly } = form.filter
  const { user, week, update } = useDrillDown()
  const {
    data: current,
    previousData,
    loading,
  } = useQuery(DRIVER_WORK_YEAR_QUERY, {
    variables: { year, ownedVehiclesOnly },
  })
  const data = current ?? previousData
  const rows = useMemo(
    () => filterDrivers(data?.driverWorkYear ?? [], drivers),
    [data, drivers]
  )
  const driverName = data?.driverWorkYear.find(row => row.user.id === user)
    ?.user.fullName

  return (
    <DriverWorkLayout
      form={form}
      onYearChange={() => update({ week: null }, { keepScroll: true })}
      driverName={driverName}
      driverFilter={!user}
      headerActions={
        user && (
          <button
            type="button"
            onClick={() => update({ user: null, week: null })}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-stone-600 hover:text-stone-900"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            {translations.allDrivers}
          </button>
        )
      }
    >
      {user ? (
        <DriverYear
          user={user}
          year={year}
          week={week}
          onSelectWeek={selected =>
            update({ week: selected }, { keepScroll: true })
          }
        />
      ) : (
        <DriverWorkYearTable
          rows={rows}
          loading={loading && !data}
          onSelectDriver={selected => update({ user: selected, week: null })}
        />
      )}
    </DriverWorkLayout>
  )
}
